import { Fragment, useEffect, useId, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { CaretDownIcon } from '@/components/layout/navIcons'
import { matchesNavGate, USER_MENU_ITEMS } from '@/components/layout/navigation'
import { usePermission } from '@/hooks/usePermission'
import { useAuth } from '@/providers/authContext'
import { paths } from '@/router/paths'
import { cn } from '@/utils/cn'
import { initials } from '@/utils/format'

/** One menu row. Inset from the panel edge so the hover ground reads as a pill. */
const ITEM_CLASS = cn(
  'block rounded-lg px-3 py-2 text-sm text-fg-muted transition-colors',
  'hover:bg-primary-subtle hover:text-primary',
)

/**
 * Topbar user menu: avatar, name and a dropdown of the administration screens
 * plus sign-out.
 *
 * Not built on a shared primitive because there is no Menu in
 * `src/components/ui/` yet. When row-action menus land in `DataTable` this
 * should be promoted to `components/ui/Menu` and reused, not duplicated.
 */
export function UserMenu() {
  const { user, logout } = useAuth()
  const { can, canViewModule, isSuperadmin } = usePermission()
  const navigate = useNavigate()
  const menuId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const handleLogout = () => {
    setIsLoggingOut(true)
    void logout().finally(() => {
      setIsLoggingOut(false)
      setIsOpen(false)
      // A deliberate sign-out, so tell the login screen to confirm it.
      void navigate(paths.login, { replace: true, state: { loggedOut: true } })
    })
  }

  // Escape closes and returns focus; a click elsewhere just closes.
  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setIsOpen(false)
      triggerRef.current?.focus()
    }

    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return
      setIsOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [isOpen])

  // Same gate rule as the sidebar rail — see `matchesNavGate`.
  const items = USER_MENU_ITEMS.filter((item) =>
    matchesNavGate(item, { can, canViewModule, isSuperadmin }),
  )
  const displayName = user?.fullName?.trim() || '—'

  return (
    <div ref={containerRef} className="relative flex h-full items-center">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        className="flex items-center gap-2 rounded-md py-1.5 pl-1.5 pr-2 text-topbar-fg transition-colors hover:bg-surface-muted"
      >
        {/* Initials only: the API exposes no avatar. Restore the image branch
            when the user-profile module adds one. */}
        <span
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sidebar-active text-sm font-semibold text-sidebar-active-fg"
        >
          {initials(user?.fullName)}
        </span>
        {/* Name over the tenant, per the design. */}
        <span className="hidden min-w-0 text-left sm:block">
          <span className="block max-w-32 truncate text-sm font-semibold text-topbar-fg-strong">
            {displayName}
          </span>
          <span className="block text-xs text-topbar-fg">Turon Travel</span>
        </span>
        <CaretDownIcon
          className={cn('size-4 shrink-0 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <div
          id={menuId}
          role="menu"
          aria-label="Account and administration"
          className="absolute top-full right-0 z-50 mt-2 min-w-60 rounded-xl border border-border bg-surface p-1.5 shadow-lg shadow-fg/5"
        >
          {/* No name / email header (client request, 2026-10-06) — the
              trigger already shows who is signed in. */}
          {items.map((item, index) => (
            <Fragment key={item.to}>
              {/* The rule is its own element, not a border on the row: it must
                  span the panel's full width while every row's hover pill stays
                  equally inset. `-mx-1.5` cancels the panel's padding. Never
                  above the first row — nothing precedes it to separate. */}
              {item.startsGroup && index > 0 && (
                <hr className="-mx-1.5 my-1.5 border-t border-border" />
              )}
              <Link
                to={item.to}
                role="menuitem"
                onClick={() => setIsOpen(false)}
                className={ITEM_CLASS}
              >
                {item.label}
              </Link>
            </Fragment>
          ))}

          <hr className="-mx-1.5 my-1.5 border-t border-border" />

          {/* Signs out immediately — no confirmation step. `isLoggingOut`
              guards against a second click while the request is in flight. */}
          <button
            type="button"
            role="menuitem"
            disabled={isLoggingOut}
            onClick={handleLogout}
            className={cn(
              ITEM_CLASS,
              'w-full text-left',
              'text-danger hover:bg-danger-subtle hover:text-danger',
              'disabled:pointer-events-none disabled:opacity-60',
            )}
          >
            Log Out
          </button>
        </div>
      )}
    </div>
  )
}
